import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, AlertTriangle, ClipboardPaste, Lock, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import LoadingStepper from "@/components/LoadingStepper";
import ClickSpark from "@/components/reactbits/ClickSpark";
import { analyzeArticle, errDetail, ERROR_CODES } from "@/lib/api";
import { useThemeToken } from "@/lib/theme";
import { useAuth } from "@/context/AuthContext";

export default function AnalyzeBox({ compact = false }) {
  // Sparks are drawn on a canvas, so this one can't be a CSS variable.
  const ink = useThemeToken("--c-ink");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [showFallback, setShowFallback] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [wall, setWall] = useState(null); // 'signup' | 'limit'
  const navigate = useNavigate();
  const { usage, refreshUsage, isPro } = useAuth();

  const run = async (payload) => {
    setLoading(true); setError("");
    try {
      const report = await analyzeArticle(payload);
      await refreshUsage();
      navigate(`/report/${report.id}`);
    } catch (e) {
      const d = errDetail(e);
      if (d.code === ERROR_CODES.SIGNUP_REQUIRED) { setWall("signup"); }
      else if (d.code === ERROR_CODES.LIMIT_REACHED) { setWall("limit"); }
      // Pasting the text is the fix for a fetch that failed, not for a spent
      // budget — the same analysis gets billed either way.
      else if (d.code === ERROR_CODES.CAPACITY) { setError(d.message); toast.error("SlantScan is at capacity"); }
      else { setError(`${d.message} You can paste the article text below instead.`); setShowFallback(true); toast.error("Couldn't analyze that article"); }
      await refreshUsage();
    } finally { setLoading(false); }
  };

  const onAnalyze = () => {
    if (!url.trim()) { toast.error("Paste a news article URL first"); return; }
    run({ url: url.trim() });
  };
  const onAnalyzeText = () => {
    if (text.trim().length < 200) { toast.error("Paste at least 200 characters of article text"); return; }
    run({ url: url.trim() || null, text: text.trim() });
  };

  if (loading) return (
    <div className="rounded-2xl border border-line/10 bg-surface p-4"><LoadingStepper /></div>
  );

  const showCounter = usage && !isPro && !usage.unlimited && usage.limit != null;

  return (
    <div className="w-full" data-testid="analyze-box">
      <div className="flex flex-col gap-3 sm:flex-row">
        {/* The shell owns the animated rim; the field sits opaque on top of it.
            focus-visible:ring is dropped in favour of that rim — two rings on
            one control is one more than the eye needs, and the rim is the
            stronger signal. The shell keeps a real focus outline for anyone
            navigating by keyboard, since the sweep alone is decoration. */}
        <div className="paste-shell flex-1 rounded-xl focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ink/30">
          <Input data-testid="analyze-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onAnalyze()}
            placeholder="https://www.example.com/news-article…"
            className="h-14 w-full rounded-xl border-line/25 bg-surface px-4 text-base shadow-sm focus-visible:ring-0 focus-visible:ring-offset-0" />
        </div>
        {/* shrink-0, not the default w-full: this sits beside the URL input in
            a flex row, and a full-width wrapper would swallow it. */}
        <ClickSpark className="relative shrink-0" sparkColor={ink}
          sparkSize={10} sparkRadius={20} sparkCount={9}>
          {/* Armed once there is something to submit. Not disabled while empty:
              the empty case has a real message worth reading, and a dead button
              cannot deliver it. This only says the field is ready. */}
          <Button data-testid="analyze-submit-btn" onClick={onAnalyze}
            data-armed={url.trim() ? "" : undefined}
            className="group h-14 w-full rounded-xl bg-ink px-6 text-base font-semibold text-paper transition-[transform,background-color,box-shadow] duration-200 ease-out hover:bg-ink/85 data-[armed]:shadow-[0_0_0_3px_rgb(var(--c-ink)/0.14)]">
            Analyze
            <ArrowRight size={18} className="ml-1.5 transition-transform duration-200 ease-out group-hover:translate-x-0.5" />
          </Button>
        </ClickSpark>
      </div>

      {showCounter && (
        <p className="mt-2.5 text-sm text-faint" data-testid="scan-counter">
          <span className="font-semibold text-ink">{usage.remaining}</span> of {usage.limit}{" "}
          {usage.authenticated ? "scans left today" : "free scans left"}
          {usage.remaining === 0 && " · "}
          {usage.remaining === 0 && (
            <button onClick={() => navigate(usage.authenticated ? "/pricing" : "/signup")} className="font-semibold text-ink underline transition-opacity duration-150 ease-out active:opacity-60">
              {usage.authenticated ? "Upgrade to Pro" : "Create a free account"}
            </button>
          )}
          {/* What comes after the two, while there are still two left. The
              count on its own says how much is left and nothing about what
              happens when it runs out, so the anonymous allowance reads as a
              trial ending rather than as the first step of a free plan. */}
          {!usage.authenticated && usage.remaining > 0 && (
            <>
              {" · "}
              <button onClick={() => navigate("/signup")} data-testid="scan-counter-upsell"
                className="font-medium text-dim underline decoration-line/40 underline-offset-2 transition-opacity duration-150 ease-out hover:text-ink active:opacity-60">
                free accounts get 5 a day
              </button>
            </>
          )}
        </p>
      )}

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-300/70 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200" data-testid="analyze-error">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!compact && (
        // A 20px-tall line of text is a fine click target and a poor tap one.
        // The height comes out of the margin above it rather than being added
        // to it, so the box is the same overall height and the label sits
        // where it always did.
        <button onClick={() => setShowFallback((v) => !v)} data-testid="toggle-fallback"
          className="mt-1 inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-faint transition-[color,opacity] duration-150 ease-out hover:text-ink active:opacity-60 sm:mt-3 sm:min-h-0">
          <ClipboardPaste size={15} /> {showFallback ? "Hide" : "Or paste the article text instead"}
        </button>
      )}

      {(showFallback || compact === "text") && (
        <div className="mt-3" data-testid="fallback-textarea-wrap">
          <Textarea data-testid="analyze-text-input" value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Paste the full article text here (headline on the first line)…"
            className="min-h-[160px] rounded-xl border-line/25 text-base" />
          {/* Full width on a phone, where a button that only takes as much room
              as its label leaves the rest of the row looking like something is
              missing beside it. Back to hugging its label from sm up. */}
          <Button data-testid="analyze-text-btn" onClick={onAnalyzeText}
            className="mt-3 h-12 w-full rounded-xl bg-ink px-6 font-semibold text-paper hover:bg-ink/85 sm:w-auto">
            Analyze pasted text <ArrowRight size={18} className="ml-1.5" />
          </Button>
        </div>
      )}

      {/* Sign-up wall */}
      <Dialog open={wall === "signup"} onOpenChange={(o) => !o && setWall(null)}>
        <DialogContent className="sm:max-w-md" data-testid="signup-wall">
          <DialogHeader>
            <div className="mx-auto mb-1 grid h-12 w-12 place-items-center rounded-full bg-subtle text-ink"><Lock size={22} /></div>
            <DialogTitle className="text-center font-serif text-2xl">Create a free account to keep scanning</DialogTitle>
            <DialogDescription className="text-center">You've used your free scans. Sign up in seconds for 5 scans every day — no card needed.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Button onClick={() => navigate("/signup")} data-testid="wall-signup-btn" className="h-12 rounded-xl bg-ink text-paper hover:bg-ink/85">Create free account</Button>
            <Button onClick={() => navigate("/signin")} variant="outline" className="h-12 rounded-xl border-line/25">I already have an account</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Daily limit wall */}
      <Dialog open={wall === "limit"} onOpenChange={(o) => !o && setWall(null)}>
        <DialogContent className="sm:max-w-md" data-testid="limit-wall">
          <DialogHeader>
            <div className="mx-auto mb-1 grid h-12 w-12 place-items-center rounded-full bg-subtle text-ink"><Zap size={22} /></div>
            <DialogTitle className="text-center font-serif text-2xl">You've hit today's free limit</DialogTitle>
            <DialogDescription className="text-center">Free accounts get 5 scans a day. Upgrade to Pro for unlimited scans, all highlights and compare mode.</DialogDescription>
          </DialogHeader>
          <Button onClick={() => navigate("/pricing")} data-testid="limit-upgrade-btn" className="h-12 rounded-xl bg-ink text-paper hover:bg-ink/85">See Pro</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
